import { ProfileMinimalOutSchemaV2, ProfileOutSchemaV2 } from "@/api/client";
import { handleError } from "@/api/handleError";
import { getTeamMemberV2 } from "@/api/requests/teamMembers";
import { trackAmplitudeEvent } from "@/helpers/integrations/analytics/amplitude";
import { AMPLITUDE_EVENTS } from "@/helpers/integrations/analytics/events";
import { LiveStatus } from "@/helpers/types/activity";
import { CallReceiver, InviteTeamMemberPayload } from "@/helpers/types/teams";
import { defaultNewInvitePayload } from "@/helpers/utils";
import { defineStore } from "pinia";
import { useBusinessStore } from "./businesses.store";
import { useNumberStore } from "./numbers.store";
import { useUsersStore } from "./users.store";

interface TeamState {
  teamMembers: ProfileOutSchemaV2[];
  teamMembersList: ProfileMinimalOutSchemaV2[];
  showInvitationModal: boolean;
  selectedTeamMember?: number | null;
  selectedTeamMemberReceiverId?: number | null;
  refetchCallQueue?: boolean;
  hasSuccessfullyInvitedMember?: boolean;
  getRecieversUnderABusiness: CallReceiver[]; // used for transfer
  selectedContactQueueTeamMembers?: [];
  newTeamMemberDetails: InviteTeamMemberPayload;
  pendingInvite: boolean;
  liveStatusDict: Record<string, LiveStatus>;
  showTeamMemberRecords: boolean; // used to show team member details in the modal
}

export const useTeamStore = defineStore("teammember", {
  state: (): TeamState => ({
    teamMembers: [],
    teamMembersList: [],
    showInvitationModal: false,
    selectedTeamMember: null, // this is the user id of the selected team member
    selectedTeamMemberReceiverId: null, // this is the receiver id of the selected team member
    refetchCallQueue: false,
    hasSuccessfullyInvitedMember: false,
    getRecieversUnderABusiness: [],
    selectedContactQueueTeamMembers: [],
    newTeamMemberDetails: defaultNewInvitePayload(),
    pendingInvite: false,
    liveStatusDict: {},
    showTeamMemberRecords: false,
  }),

  getters: {
    getAllTeamMembers: (state: TeamState) => {
      const numberStore = useNumberStore();

      return [...state.teamMembers, numberStore.activeNumber];
    },
    activeTeamMembers: (state: TeamState) => {
      // move this to store to be reused anywhere
      const results = state.teamMembers
        .filter((member) => state.liveStatusDict[member.user]?.live)
        .slice(0, 2); // Default to the first 3 active members

      // If no active members are found, return an empty array
      if (results.length < 1) {
        return [];
      }

      return results;
    },
    getAllReceiversUnderANumberWithActiveState: (state: TeamState) => {
      const userStore = useUsersStore();
      const matchedValue = state.getRecieversUnderABusiness
        // to make sure we map the active state based on the users that are online
        .map((receiver: CallReceiver) => {
          return {
            ...receiver,
            is_active: state.liveStatusDict[receiver?.user.id]?.live || false,
          };
        })
        // to make sure its not the current user
        .filter(
          (receiver) =>
            receiver.is_active && receiver.user.id !== userStore.currentUser?.id
        );
      return matchedValue;
    },
    getUserIdTeamMemberMap: (state: TeamState) => {
      const map: Record<string, string> = {};
      state.teamMembers.map((member) => {
        const userId = member.user;
        const name = `${member.first_name || ""} ${member.last_name || ""}`;
        if (userId && name) {
          map[userId] = name;
        }
      });
      return map;
    },
  },
  actions: {
    async showTeamModal() {
      this.showInvitationModal = true;

      await trackAmplitudeEvent(AMPLITUDE_EVENTS.ADD_TEAM_MEMBER_CLICKED, {
        is_onboard_page: false,
      });
    },
    async getTeamMember(teamMemberId: number) {
      const businessStore = useBusinessStore();
      const businessId = businessStore.currentBusinessId;

      try {
        if (!teamMemberId || !businessId) {
          return;
        }
        const res = await getTeamMemberV2({
          businessId,
          userId: teamMemberId,
        });
        if (res.data?.permission) {
          this.teamMembersList.push(res.data.profile);
          this.teamMembers.push(res.data.profile);
        }
        return res.data;
      } catch (error) {
        handleError({
          error,
          silent: true,
          fallbackMessage: "Unable to get permissions",
        });
      }
    },
  },
  persist: {
    storage: localStorage,
    paths: [
      "hasSuccessfullyInvitedMember",
      "newTeamMemberDetails",
      "pendingInvite",
      "showInvitationModal",
    ],
  },
});
